import { ConfirmationAction } from '@app/components/ConfirmationAction';
import { LDateTime } from '@app/components/date/LDateTime';
import { UserLabel } from '@app/components/users/UserLabel';
import { useToast } from '@app/core/notifications';
import { socialPlatforms } from '@app/modules/social-export/domain/platforms';
import {
  socialExportPublishCardMessages,
  socialExportPublishMessages,
} from '@app/modules/social-export/publish';
import { socialExport } from '@mi-front/api-clients';
import { QueryView } from '@mi-front/ui';
import BuildIcon from '@mui/icons-material/Build';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Button, Stack } from '@mui/material';
import AlertTitle from '@mui/material/AlertTitle';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';

export function SocialUploadCompletedStatus({ upload }: { upload: socialExport.UploadDto }) {
  const { $t } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const queryClient = useQueryClient();
  const platformName = socialPlatforms.get(upload.platform).name;

  const platformFeaturesQuery = socialExport.useGetUploadPlatformFeatures(upload.id);

  const deleteMutation = socialExport.useDeleteUpload({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: socialExport.getUploadByKeyQueryKey(upload.key, upload.platform),
        });
        notifySuccess($t(socialExportPublishMessages.videoDeletedFromPlatform));
      },
      onError: (error: Error) => {
        notifyError(error?.message || $t(socialExportPublishMessages.errorDeletingVideo));
      },
    },
  });

  const handleDelete = async () => {
    await deleteMutation.mutateAsync({ id: upload.id });
  };

  const completedData = upload.data as socialExport.UploadCompletedDataDto;

  return (
    <QueryView query={platformFeaturesQuery} inline>
      {(platformFeatures: socialExport.UploadPlatformFeaturesDto) => {
        const hasManageUrl =
          platformFeatures.manageUrl && platformFeatures.manageUrl !== platformFeatures.publicUrl;

        const hasActions =
          platformFeatures.publicUrl || hasManageUrl || platformFeatures.isDeletable;

        return (
          <Alert severity="success">
            <AlertTitle>
              <FormattedMessage {...socialExportPublishMessages.uploadDetailPublishedTitle} />
            </AlertTitle>
            <Box mt={1}>
              <FormattedMessage {...socialExportPublishMessages.publishedOn} />{' '}
              <strong>
                <LDateTime value={completedData.publishedAt} showRelative="inline" />
              </strong>
            </Box>
            <Box mt={1}>
              <FormattedMessage
                {...socialExportPublishMessages.createdBy}
                values={{
                  user: (
                    <strong>
                      <UserLabel id={upload.createdBy} />
                    </strong>
                  ),
                }}
              />
            </Box>
            {hasActions && (
              <Box mt={2}>
                <Stack direction="row" spacing={2}>
                  {platformFeatures.publicUrl && (
                    <Button
                      size="small"
                      variant="outlined"
                      component="a"
                      href={platformFeatures.publicUrl}
                      target="_blank"
                      rel="noreferrer"
                      startIcon={<OpenInNewIcon />}
                    >
                      <FormattedMessage {...socialExportPublishCardMessages.viewVideo} />
                    </Button>
                  )}
                  {hasManageUrl && platformFeatures.manageUrl && (
                    <Button
                      size="small"
                      variant="outlined"
                      component="a"
                      href={platformFeatures.manageUrl}
                      target="_blank"
                      rel="noreferrer"
                      startIcon={<BuildIcon />}
                    >
                      <FormattedMessage
                        {...socialExportPublishCardMessages.manageVideo}
                        values={{ platform: platformName }}
                      />
                    </Button>
                  )}
                  {platformFeatures.isDeletable && (
                    <ConfirmationAction
                      title={$t(socialExportPublishCardMessages.deleteVideoFromPlatformTitle, {
                        platform: platformName,
                      })}
                      message={
                        <FormattedMessage
                          {...socialExportPublishCardMessages.deleteVideoFromPlatformMessage}
                        />
                      }
                      confirmLabel={$t({
                        id: 'common.delete',
                        defaultMessage: 'Eliminar',
                      })}
                      cancelLabel={$t({
                        id: 'common.cancel',
                        defaultMessage: 'Cancelar',
                      })}
                      confirmColor="error"
                      onConfirm={handleDelete}
                    >
                      {({ onClick }) => (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={onClick}
                          disabled={deleteMutation.isPending}
                        >
                          <FormattedMessage
                            {...socialExportPublishCardMessages.deleteFromPlatform}
                            values={{ platform: platformName }}
                          />
                        </Button>
                      )}
                    </ConfirmationAction>
                  )}
                </Stack>
              </Box>
            )}
          </Alert>
        );
      }}
    </QueryView>
  );
}
